import {memo, useCallback} from 'react'
import {useLocation, useNavigate} from 'react-router-dom'

import {useBooleanQueryParam, useChangeId, useQueryParam} from '../../../hooks/routes'
import useDocumentTitle from '../../../hooks/useDocumentTitle'
import {getChangesArg} from '../../../rest/arguments'
import {getHrefWithQueryParams} from '../../../routes'
import {restApi} from '../../../services/rest'
import type {ChangeId} from '../../../types'
import {stringifyId, toBuildTypeId} from '../../../types'
import {isValue} from '../../../utils/guards'
import ErrorPage from '../../common/ErrorPage/ErrorPage'
import Loader from '../../common/Loader/Loader'
import {ChangeBuildsContext} from '../../packages/Changes/ChangeDetailsTabs/ChangeBuilds/ChangeBuilds.context'
import ChangeDetailsTabs from '../../packages/Changes/ChangeDetailsTabs/ChangeDetailsTabs'
import {ChangePageTabName} from '../../packages/Changes/ChangeDetailsTabs/ChangeDetailsTabs.types'
import {getChange} from '../../packages/Changes/Changes.selectors'

import {usePageTitle} from './ChangePage.hooks'
import ChangePageHeader from './ChangePageHeader'

import styles from './ChangePage.css'

import {useAppSelector} from 'src/hooks/react-redux'

type Props = {
  changeId: ChangeId
}

const context = {withInfiniteScroll: true, pageSize: 50}

function ChangePage({changeId}: Props) {
  const navigate = useNavigate()
  const location = useLocation()
  const pageTitle = usePageTitle(changeId)

  const personal = useBooleanQueryParam('personal')
  const buildTypeIdString = useQueryParam('buildTypeId')
  const buildTypeId = buildTypeIdString != null ? toBuildTypeId(buildTypeIdString) : null
  const currentTabString = useQueryParam('tab')
  const currentTab = isValue(ChangePageTabName, currentTabString)
    ? currentTabString
    : ChangePageTabName.FILES

  const locator = `id:${stringifyId(changeId)},personal:${personal}`
  const {isLoading} = restApi.endpoints.getAllChanges.useQuery(getChangesArg(locator), {
    selectFromResult: ({isLoading}) => ({isLoading}),
  })
  const change = useAppSelector(state => getChange(state, changeId))

  const getTabLink = useCallback(
    (tabId: string) =>
      getHrefWithQueryParams(location, null, params => ({...params, tab: stringifyId(tabId)})),
    [location],
  )
  const onSelectTab = useCallback(
    (tabId: string) => navigate(getTabLink(tabId)),
    [navigate, getTabLink],
  )

  useDocumentTitle(
    isLoading
      ? 'TeamCity Loading...'
      : `Change page${pageTitle != null ? `: ${pageTitle}` : ''} — TeamCity`,
  )

  if (isLoading) {
    return <Loader className={styles.pageLoader} />
  }

  if (!window.GLOBAL_ERROR_PAGE_DISABLED && changeId != null && change == null) {
    const message = `Change with\u00a0id '${stringifyId(changeId)}' does\u00a0not\u00a0exist`
    return <ErrorPage notFound error={message} />
  }

  return (
    <div className={styles.page}>
      <ChangePageHeader changeId={changeId} />
      <ChangeBuildsContext.Provider value={context}>
        <ChangeDetailsTabs
          changeId={changeId}
          buildTypeId={buildTypeId}
          currentTab={currentTab}
          onSelectTab={onSelectTab}
          withHref
        />
      </ChangeBuildsContext.Provider>
    </div>
  )
}

function ChangePageWrapper() {
  const changeId = useChangeId()
  return changeId != null ? <ChangePage changeId={changeId} /> : null
}

export default memo<{}>(ChangePageWrapper)
